import { Calendar as CalendarSlice, Hook, Toast } from 'jamespot-front-business';
import { CalendarApi, CalendarEvent, CalendarView, JRCCalendarProps, Utils } from 'jamespot-react-components';
import jApi from 'jamespot-user-api';
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CommentsBloc } from '../../components/CommentsBloc';
import useCalendarRT from '../../hooks/useCalendarRT';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { commonComponentsRegistry } from '../../registry/CommonRegistry';
import { CalendarHeader } from './CalendarHeader';
import { CalendarToolbar } from './CalendarToolbar';

type Filter = ('socialEvent' | 'meeting' | 'animatedMeeting' | 'boardCard')[];

const Calendar = commonComponentsRegistry.getLazyComponent('Calendar');

const CommentsContainer = styled.div`
    margin-bottom: ${({ theme }) => theme.space.md}px;
`;

const Header = styled.div<{ view: CalendarView }>`
    height: 54px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    ${({ view, theme }) => view === 'list' && `border-bottom: 1px solid ${theme.color.grey2};`}
    padding: 0 ${({ theme }) => theme.space.md}px;
`;

const options: { label: string; value: CalendarView }[] = [
    {
        label: 'CALENDAR_dayGridMonth',
        value: 'dayGridMonth',
    },
    {
        label: 'CALENDAR_timeGridWeek',
        value: 'timeGridWeek',
    },
    {
        label: 'CALENDAR_timeGridDay',
        value: 'timeGridDay',
    },
    {
        label: 'CALENDAR_listWeek',
        value: 'list',
    },
];

export const CalendarWrapper = ({
    views = ['dayGridMonth', 'list', 'timeGridDay', 'timeGridWeek'],
    mode = 'full',
    withPadding = true,
    withHeader = true,
    withToolbar = true,
    fluid,
    children,
    onSelect,
}: {
    views: Array<'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'list'>;
    mode?: JRCCalendarProps['mode'];
    fluid?: boolean;
    withPadding?: boolean;
    withHeader?: boolean;
    withToolbar?: boolean;
    onSelect?: (params: { start: Date; end: Date; allDay: boolean } | null) => void;
    children?({
        dates,
        eventToUpdate,
        creationType,
        isExportModalOpen,
        setCreationType,
        setEventToUpdate,
        setExportModalOpen,
    }: {
        dates?: { start: string; end: string };
        eventToUpdate?: CalendarEvent | null;
        creationType: string | null;
        isExportModalOpen: boolean;
        setEventToUpdate: React.Dispatch<React.SetStateAction<CalendarEvent | null>>;
        setCreationType: React.Dispatch<React.SetStateAction<'socialEvent' | 'meeting' | 'animatedMeeting' | null>>;
        setExportModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    }): ReactNode;
}) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    useCalendarRT();
    const [label, setLabel] = useState<string>();
    const { dates, loading, list, pendingStatusChanges } = useAppSelector(CalendarSlice.selectors.selectCalendarEvents);
    const [calendarApi, setCalendarApi] = useState<CalendarApi>();
    const [eventToUpdate, setEventToUpdate] = useState<CalendarEvent | null>(null);
    const [isExportModalOpen, setExportModalOpen] = useState(false);
    const globalDefaultView = useAppSelector((state) => Hook.selectors.selectHook(state, 'calendarHook'))?.defaultView;
    const defaultView = useMemo(() => {
        const urlQuery = new URLSearchParams(window.location.search).get('view');
        switch (urlQuery) {
            case 'dayGridMonth':
            case 'timeGridDay':
            case 'timeGridWeek':
            case 'list':
                return urlQuery;
            default:
                return globalDefaultView === 'calendar' ? 'dayGridMonth' : 'list';
        }
    }, [globalDefaultView]);

    const viewsOptions = useMemo<{ label: string; value: CalendarView }[]>(
        () =>
            options
                .filter((el) => views.includes(el.value))
                .map((option) => {
                    return { label: intl.formatMessage({ id: option.label }), value: option.value };
                }),
        [views, intl],
    );
    const [view, setView] = useState<(typeof viewsOptions)[number]>(
        viewsOptions.find((v) => v.value === defaultView) ?? viewsOptions[0],
    );

    const navigateWithQuery = useCallback(
        (queries: { types?: Filter; view?: CalendarView }) => {
            const params = new URLSearchParams(window.location.search);
            if (queries.types !== undefined) {
                if (queries.types.length > 0) {
                    params.set('types', queries.types.join(','));
                } else {
                    params.delete('types');
                }
            }
            if (queries.view !== undefined) {
                if (queries.view) {
                    params.set('view', queries.view);
                } else {
                    params.delete('view');
                }
            }
            navigate(`?${params.toString()}`, { replace: true });
        },
        [navigate],
    );

    const onError = useCallback(
        (err?: string) => {
            dispatch(Toast.actions.error({ label: err ?? intl.formatMessage({ id: 'GLOBAL_Error' }) }));
        },
        [dispatch, intl],
    );

    const onSuccess = useCallback(
        (message: string) => {
            dispatch(Toast.actions.success({ label: message }));
        },
        [dispatch],
    );

    const handleChangeView = useCallback(
        (view: { label: string; value: CalendarView }) => {
            setView(view);
            navigateWithQuery({ view: view.value });
        },
        [navigateWithQuery],
    );

    const handleChangeDates = useCallback(
        (start: Date, end: Date) => {
            const dates = { start: Utils.date.formatDate(start), end: Utils.date.formatDate(end) };
            dispatch(CalendarSlice.actions.setDates(dates));
        },
        [dispatch],
    );

    const [creationType, setCreationType] = useState<'socialEvent' | 'meeting' | 'animatedMeeting' | null>(null);

    useEffect(() => {
        dispatch(
            CalendarSlice.actions.setTypes(
                (new URLSearchParams(window.location.search)
                    .get('types')
                    ?.split(',')
                    .filter((type) =>
                        ['socialEvent', 'meeting', 'animatedMeeting', 'boardCard'].includes(type),
                    ) as Filter) ?? [],
            ),
        );
    }, [dispatch]);

    return (
        <Fragment>
            {children &&
                children({
                    dates,
                    eventToUpdate,
                    creationType,
                    isExportModalOpen,
                    setEventToUpdate,
                    setCreationType,
                    setExportModalOpen,
                })}
            {(withHeader || withToolbar) && (
                <Header view={view.value}>
                    {withHeader && <CalendarHeader />}
                    {withToolbar && (
                        <CalendarToolbar
                            label={label}
                            isLoading={loading === 'pending'}
                            view={view}
                            calendarApi={calendarApi}
                            viewsOptions={viewsOptions}
                            navigateWithQuery={navigateWithQuery}
                            setView={setView}
                            setCreationType={setCreationType}
                            setExportModalOpen={setExportModalOpen}
                        />
                    )}
                </Header>
            )}
            <Calendar
                fluid={fluid}
                mode={mode}
                fullScreen
                withPadding={withPadding}
                events={list}
                loading={loading === 'pending'}
                handler={{
                    global: jApi.global,
                    calendar: jApi.calendar,
                    article: jApi.article,
                }}
                defaultView={view.value}
                eventModalExtraComponent={({ id, uri, _right }) => {
                    return (
                        <CommentsContainer>
                            <CommentsBloc articleId={id} articleUri={uri} canComment={_right.comment} />
                        </CommentsContainer>
                    );
                }}
                deportedToolbar={{
                    onChangeLabel: setLabel,
                    onCalendarApiInit: setCalendarApi,
                    view,
                }}
                onChangeDates={handleChangeDates}
                onError={onError}
                onSuccess={onSuccess}
                onChangeView={handleChangeView}
                onDeleteEvent={() => {
                    dispatch(
                        Toast.actions.success({ label: intl.formatMessage({ id: 'GLOBAL_Successfully_Deleted' }) }),
                    );
                }}
                onClickUpdateEvent={setEventToUpdate}
                onUpdateStatus={(params) => dispatch(CalendarSlice.actions.changeResponse(params))}
                isUpdateStatusLoading={(id) => pendingStatusChanges.some((psc) => psc.eventId === id)}
                onSelect={onSelect}
            />
        </Fragment>
    );
};
